import { secsToHHMM } from "./ui";

export interface Bar { label: string; value: number; sub?: string; highlight?: boolean; }

/** Vertical bar chart, dependency-free SVG. Good for distributions/comparisons. */
export function Bars({ data, height = 200, valueLabel }: {
  data: Bar[]; height?: number; valueLabel?: (v: number) => string;
}) {
  if (data.length === 0) return <div className="notice">No data in range.</div>;
  const w = Math.max(360, data.length * 46);
  const pad = 28;
  const max = Math.max(...data.map((d) => d.value), 1);
  const bw = (w - pad * 2) / data.length;
  const fmt = valueLabel ?? ((v: number) => String(v));
  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} role="img">
        <line x1={pad} y1={height - pad} x2={w - pad} y2={height - pad} stroke="var(--border)" />
        {data.map((d, i) => {
          const bh = (d.value / max) * (height - pad * 2);
          const x = pad + i * bw + bw * 0.18;
          const y = height - pad - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={bw * 0.64} height={Math.max(bh, 0)} rx={3}
                    fill={d.highlight ? "var(--brand)" : "var(--muted)"}>
                <title>{`${d.label}: ${fmt(d.value)}`}</title>
              </rect>
              {d.value > 0 && (
                <text x={x + bw * 0.32} y={y - 4} fontSize="9" textAnchor="middle" fill="var(--text)">
                  {fmt(d.value)}
                </text>
              )}
              <text x={x + bw * 0.32} y={height - pad + 12} fontSize="9" textAnchor="middle" fill="var(--muted)">
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export const hoursLabel = (sec: number) => secsToHHMM(sec);

/** Monthly calendar heatmap for an employee. byDate: 'YYYY-MM-DD' -> inside_hhmm/status. */
export function MonthCalendar({ year, month, byDate, onPickDate }: {
  year: number; month: number;
  byDate: Record<string, { inside_hhmm: string; inside_seconds: number; status: string }>;
  onPickDate?: (d: string) => void;
}) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const startDow = (first.getUTCDay() + 6) % 7; // Monday-first
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const maxSec = Math.max(1, ...Object.values(byDate).map((v) => v.inside_seconds));
  const cells: (number | null)[] = [...Array(startDow).fill(null),
    ...Array.from({ length: days }, (_, i) => i + 1)];
  const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, fontSize: 11 }}>
        {names.map((n) => <div key={n} className="notice" style={{ padding: 2 }}>{n}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const key = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const rec = byDate[key];
          const intensity = rec ? 0.15 + 0.85 * (rec.inside_seconds / maxSec) : 0;
          const bg = rec
            ? `color-mix(in srgb, var(--brand) ${Math.round(intensity * 100)}%, transparent)`
            : "var(--surface-2)";
          return (
            <div key={i} title={rec ? `${key}: ${rec.inside_hhmm} (${rec.status})` : key}
                 onClick={() => rec && onPickDate?.(key)}
                 style={{ background: bg, borderRadius: 6, padding: "6px 4px", minHeight: 44,
                          cursor: rec && onPickDate ? "pointer" : "default",
                          border: "1px solid var(--border)" }}>
              <div style={{ fontWeight: 600 }}>{d}</div>
              {rec && <div style={{ fontSize: 10 }}>{rec.inside_hhmm}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Employee x day heatmap (dependency-free). Cells shaded by inside-office time. */
export function Heatmap({ dates, employees, onPick }: {
  dates: string[];
  employees: { employee_id: string; employee_name: string; by_date: Record<string, number> }[];
  onPick?: (empId: string) => void;
}) {
  if (dates.length === 0 || employees.length === 0)
    return <div className="notice">No data in range.</div>;
  const max = Math.max(1, ...employees.flatMap((e) => Object.values(e.by_date)));
  const cell = 15;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "separate", borderSpacing: 2, width: "auto" }}>
        <thead>
          <tr>
            <th style={{ position: "sticky", left: 0, background: "var(--surface)" }} />
            {dates.map((d) => (
              <th key={d} style={{ fontSize: 8, color: "var(--muted)", padding: 0,
                width: cell, writingMode: "vertical-rl", textTransform: "none" }}>{d.slice(5)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.map((e) => (
            <tr key={e.employee_id}>
              <td onClick={() => onPick?.(e.employee_id)} title={e.employee_id}
                  style={{ fontSize: 11, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden",
                    textOverflow: "ellipsis", padding: "0 8px 0 2px", border: "none",
                    position: "sticky", left: 0, background: "var(--surface)",
                    cursor: onPick ? "pointer" : "default", color: onPick ? "var(--brand)" : "var(--text)" }}>
                {e.employee_name || e.employee_id}
              </td>
              {dates.map((d) => {
                const s = e.by_date[d] || 0;
                const op = s ? 0.14 + 0.86 * (s / max) : 0;
                return (
                  <td key={d} title={`${e.employee_name} · ${d}: ${s ? secsToHHMM(s) : "—"}`}
                      style={{ width: cell, height: cell, padding: 0, borderRadius: 3, border: "none",
                        background: s ? `color-mix(in srgb, var(--brand) ${Math.round(op * 100)}%, transparent)`
                          : "var(--surface-2)" }} />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Period preset selector; computes a [from,to] range and reports the choice. */
export type PeriodName = "daily" | "weekly" | "monthly" | "quarterly" | "yearly" | "custom";

export function PeriodSelector({ value, onChange }: {
  value: PeriodName; onChange: (p: PeriodName, from?: string, to?: string) => void;
}) {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const pick = (p: PeriodName) => {
    const now = new Date();
    const end = iso(now);
    let from: string | undefined;
    if (p === "daily") from = end;
    else if (p === "weekly") from = iso(new Date(now.getTime() - 6 * 864e5));
    else if (p === "monthly") from = iso(new Date(now.getFullYear(), now.getMonth(), 1));
    else if (p === "quarterly") from = iso(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1));
    else if (p === "yearly") from = iso(new Date(now.getFullYear(), 0, 1));
    onChange(p, from, p === "custom" ? undefined : end);
  };
  const opts: PeriodName[] = ["daily", "weekly", "monthly", "quarterly", "yearly", "custom"];
  return (
    <div className="row" role="tablist" aria-label="Reporting period">
      {opts.map((p) => (
        <button key={p} className={value === p ? "primary" : "ghost"}
                onClick={() => pick(p)} style={{ textTransform: "capitalize" }}>
          {p}
        </button>
      ))}
    </div>
  );
}

/** Small previous-period delta badge. */
export function Delta({ pct }: { pct: number | null | undefined }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span style={{ fontSize: 12, color: up ? "var(--ok)" : "var(--incomplete)" }}>
      {up ? "▲" : "▼"} {Math.abs(pct)}% vs prev
    </span>
  );
}
